import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { formatCurrency, formatDate } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Reports({ user, onLogout }) {
  const [reportType, setReportType] = useState('attendance');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  
  const [filters, setFilters] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    employee_id: ''
  });

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = '';
      
      switch(reportType) {
        case 'attendance':
          url = `${API}/reports/attendance?start_date=${filters.start_date}&end_date=${filters.end_date}`;
          if (filters.employee_id) url += `&employee_id=${filters.employee_id}`;
          break;
        case 'payroll':
          url = `${API}/reports/payroll?month=${filters.month}&year=${filters.year}`;
          break;
        case 'leaves':
          url = `${API}/reports/leaves?start_date=${filters.start_date}&end_date=${filters.end_date}`;
          if (filters.employee_id) url += `&employee_id=${filters.employee_id}`;
          break;
        case 'employees':
          url = `${API}/reports/employees`;
          break;
        default:
          return;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportData(response.data);
    } catch (error) {
      alert('Failed to generate report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = '';
      
      switch(reportType) {
        case 'attendance':
          url = `${API}/reports/attendance?start_date=${filters.start_date}&end_date=${filters.end_date}&export_csv=true`;
          if (filters.employee_id) url += `&employee_id=${filters.employee_id}`;
          break;
        case 'payroll':
          url = `${API}/reports/payroll?month=${filters.month}&year=${filters.year}&export_csv=true`;
          break;
        case 'leaves':
          url = `${API}/reports/leaves?start_date=${filters.start_date}&end_date=${filters.end_date}&export_csv=true`;
          if (filters.employee_id) url += `&employee_id=${filters.employee_id}`;
          break;
        case 'employees':
          url = `${API}/reports/employees?export_csv=true`;
          break;
        default:
          return;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${reportType}_report.csv`;
      link.click();
      
      alert('Report exported successfully!');
    } catch (error) {
      alert('Failed to export report: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <h1 className="text-3xl font-bold mb-6" style={{color: '#2b3380'}}>Reports & Export</h1>

        {/* Reports Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="Reports Overview"
            icon="📊"
            bgColor="#607d8b"
            stats={[
              {
                label: 'Total Employees',
                value: dashboardStats.total_employees || 0,
                subtitle: 'Report subjects',
                color: '#2b3380'
              },
              {
                label: 'Attendance Rate',
                value: `${dashboardStats.attendance_percentage || 0}%`,
                subtitle: 'Overall',
                color: '#4caf50'
              },
              {
                label: 'Payroll Amount',
                value: `₹${(dashboardStats.total_payroll_amount / 100000).toFixed(1)}L`,
                subtitle: 'Current month',
                color: '#9c27b0'
              },
              {
                label: 'Report Types',
                value: '4',
                subtitle: 'Available reports',
                color: '#607d8b'
              }
            ]}
          />
        )}

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">Report Type</label>
              <select
                value={reportType}
                onChange={(e) => { setReportType(e.target.value); setReportData([]); }}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="attendance">Attendance Report</option>
                <option value="payroll">Payroll Report</option>
                <option value="leaves">Leave Report</option>
                <option value="employees">Employee Summary</option>
              </select>
            </div>

            {(reportType === 'attendance' || reportType === 'leaves') && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Start Date</label>
                  <input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">End Date</label>
                  <input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
              </>
            )}

            {reportType === 'payroll' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Month</label>
                  <select
                    value={filters.month}
                    onChange={(e) => setFilters({...filters, month: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    {[...Array(12)].map((_, i) => (
                      <option key={i+1} value={i+1}>
                        {new Date(2024, i).toLocaleString('default', {month: 'long'})}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Year</label>
                  <select
                    value={filters.year}
                    onChange={(e) => setFilters({...filters, year: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </>
            )}
          </div>

          <div className="mt-6 flex gap-3">
            <button
              onClick={generateReport}
              disabled={loading}
              className="px-6 py-2 rounded-lg text-white disabled:opacity-50"
              style={{backgroundColor: '#2b3380'}}
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
            {reportData.length > 0 && (
              <button
                onClick={exportReport}
                className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                style={{borderColor: '#2b3380', color: '#2b3380'}}
              >
                Export to CSV
              </button>
            )}
          </div>
        </div>

        {reportData.length > 0 && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-4 border-b" style={{backgroundColor: '#f5f7fa'}}>
              <h2 className="text-lg font-semibold">Report Results ({reportData.length} records)</h2>
            </div>
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              {reportType === 'attendance' && (
                <table className="w-full">
                  <thead style={{backgroundColor: '#2b3380', color: 'white'}}>
                    <tr>
                      <th className="px-4 py-3 text-left">Employee</th>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Check In</th>
                      <th className="px-4 py-3 text-left">Check Out</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((record, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-4 py-3">{record.employee_name}</td>
                        <td className="px-4 py-3">{formatDate(record.date)}</td>
                        <td className="px-4 py-3">{record.status}</td>
                        <td className="px-4 py-3">{record.check_in_time ? new Date(record.check_in_time).toLocaleTimeString() : '-'}</td>
                        <td className="px-4 py-3">{record.check_out_time ? new Date(record.check_out_time).toLocaleTimeString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {reportType === 'payroll' && (
                <table className="w-full">
                  <thead style={{backgroundColor: '#2b3380', color: 'white'}}>
                    <tr>
                      <th className="px-4 py-3 text-left">Employee</th>
                      <th className="px-4 py-3 text-left">Basic Salary</th>
                      <th className="px-4 py-3 text-left">Gross Salary</th>
                      <th className="px-4 py-3 text-left">Deductions</th>
                      <th className="px-4 py-3 text-left">Net Salary</th>
                      <th className="px-4 py-3 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((record, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-4 py-3">{record.employee_name}</td>
                        <td className="px-4 py-3">{formatCurrency(record.basic_salary)}</td>
                        <td className="px-4 py-3">{formatCurrency(record.gross_salary)}</td>
                        <td className="px-4 py-3">{formatCurrency(record.total_deductions)}</td>
                        <td className="px-4 py-3 font-bold">{formatCurrency(record.net_salary)}</td>
                        <td className="px-4 py-3">{record.payment_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {reportType === 'leaves' && (
                <table className="w-full">
                  <thead style={{backgroundColor: '#2b3380', color: 'white'}}>
                    <tr>
                      <th className="px-4 py-3 text-left">Employee</th>
                      <th className="px-4 py-3 text-left">Type</th>
                      <th className="px-4 py-3 text-left">Start Date</th>
                      <th className="px-4 py-3 text-left">End Date</th>
                      <th className="px-4 py-3 text-left">Days</th>
                      <th className="px-4 py-3 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((record, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-4 py-3">{record.employee_name}</td>
                        <td className="px-4 py-3">{record.leave_type}</td>
                        <td className="px-4 py-3">{formatDate(record.start_date)}</td>
                        <td className="px-4 py-3">{formatDate(record.end_date)}</td>
                        <td className="px-4 py-3">{record.number_of_days || 0}</td>
                        <td className="px-4 py-3">{record.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {reportType === 'employees' && (
                <table className="w-full">
                  <thead style={{backgroundColor: '#2b3380', color: 'white'}}>
                    <tr>
                      <th className="px-4 py-3 text-left">ID</th>
                      <th className="px-4 py-3 text-left">Name</th>
                      <th className="px-4 py-3 text-left">Email</th>
                      <th className="px-4 py-3 text-left">Designation</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Salary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((emp, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-4 py-3">{emp.id}</td>
                        <td className="px-4 py-3">{emp.first_name} {emp.last_name}</td>
                        <td className="px-4 py-3">{emp.email}</td>
                        <td className="px-4 py-3">{emp.designation}</td>
                        <td className="px-4 py-3">{emp.employee_status}</td>
                        <td className="px-4 py-3">{formatCurrency(emp.salary)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {reportData.length === 0 && !loading && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">
            <p className="text-lg">Select filters and click "Generate Report" to view data.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Reports;